import { message } from "antd";
import axios from "axios";
import authAction from "../redux/auth/actions";
import { store } from "../redux/store";
import { BASEURL_API } from "../static/data/constants";
import { CODES } from "./status-codes";
import { getSignatureHeaders, isTokenExpired, updateServerTime } from "./requestSigning";
import { clearCustomerSession, getCustomerToken } from "./customerSession";

const { logout } = authAction;

// One session per portal per browser, shared by all tabs: when another tab
// logs in, logs out or switches company on this page's portal, reload so this
// tab never shows one account's page while sending another account's token.
const SESSION_KEYS = {
	cms: "access_token",
	customer: "customer_access_token",
	tsp: "movegistics_tsp_access_token",
};
if (typeof window !== "undefined") {
	window.addEventListener("storage", (event) => {
		const path = window.location.pathname;
		const scope = path.indexOf("/tsp-portal") !== -1 ? "tsp" : path.indexOf("/customer-portal") !== -1 ? "customer" : "cms";
		if (event.key === SESSION_KEYS[scope] && event.oldValue !== event.newValue) {
			window.location.reload();
		}
	});
}

const METHOD = {
	GET: "get",
	POST: "post",
	PUT: "put",
	DELETE: "delete",
};

const BASEURL = BASEURL_API;
// const BASEURL = "http://localhost:8421/";

// CHECK BELOW FOR SAMPLE DATA TO BE PASSED
class Api {
	constructor(options = {}) {
		this.baseURL = BASEURL;
		this.authMode = options.authMode || "";
		this.getUserType();
	}

	getUserType() {
		if (localStorage.getItem("userType")) this.userType = localStorage.getItem("userType");
	}

	// URL FOR API
	// REFER SAMPLE JSON AT BOTTOM FOR DATA VALUES
	get(url, data) {
		return new Promise((resolve, reject) => {
			this.api(METHOD.GET, url, data)
				.then((response) => {
					resolve(response);
				})
				.catch((error) => {
					console.error(error);
					reject(error);
				});
		});
	}

	post(url, data) {
		return new Promise((resolve, reject) => {
			this.api(METHOD.POST, url, data)
				.then((response) => {
					resolve(response);
				})
				.catch((error) => {
					console.error(error);
					reject(error);
				});
		});
	}

	put(url, data) {
		return new Promise((resolve, reject) => {
			this.api(METHOD.PUT, url, data)
				.then((response) => {
					resolve(response);
				})
				.catch((error) => {
					console.error(error);
					reject(error);
				});
		});
	}

	delete(url, data) {
		return new Promise((resolve, reject) => {
			this.api(METHOD.DELETE, url, data)
				.then((response) => {
					resolve(response);
				})
				.catch((error) => {
					console.error(error);
				});
		});
	}

	api(method, url, data) {
		return new Promise((resolve, reject) => {
			let axiosConfig = {};
			const isTspRequest = this.isTspRequest(data);
			axiosConfig.method = method;
			axiosConfig.url = this.baseURL + url;
			axiosConfig.headers = this.setHeaders(data);
			if (isTspRequest) axiosConfig.withCredentials = true;
			if (data) {
				if (data.params) axiosConfig.params = data.params;
				if (data.data && data.data.responseType && data.data.responseType === "blob")
					axiosConfig.responseType = "blob";
				if (data.data) axiosConfig.data = data.data;
			}

			const scope = this.portalScope(data);
			const accessToken = scope === "tsp"
				? !(data && data.skipAuth) && this.getTspToken()
				: axiosConfig.headers["access_token"];
			// Sessions end after 7 days: log out instead of sending a dead token.
			if (accessToken && isTokenExpired(accessToken)) {
				this.endSession(scope);
				message.error("Your session has expired. Please log in again.");
				reject(new Error("Session expired"));
				return;
			}
			// Prove this request comes from the browser that logged in.
			const signing = accessToken
				? getSignatureHeaders(method, axiosConfig.url, accessToken, scope)
				: Promise.resolve({});

			signing
				.then((signatureHeaders) => {
					Object.assign(axiosConfig.headers, signatureHeaders);
					return axios(axiosConfig);
				})
				.then((response) => {
					updateServerTime(response.headers && response.headers["x-server-time"]);
					resolve(response.data);
				})
				.catch((error) => {
					//DEFAULT ERROR HANDLING

					if (error.response) {
						updateServerTime(error.response.headers && error.response.headers["x-server-time"]);
						if (error.response.status === CODES.UNAUTHORIZED) {
							this.endSession(scope);
							// window.location = "/";
							message.error(error.response.data.message);
						} else if (error.response.data.email) {
							message.error(error.response.data.email.msg);
						} else if (error.response.data.password) {
							message.error(error.response.data.password.msg);
						} else if (error.response.status === CODES.VALIDATION_ERROR)
							message.error(error.response.data.message);
						else if (error.response.data.message) message.error(error.response.data.message);
					}
					console.error("TCL: Api -> api -> error", error);
					reject(error);
				});
		});
	}

	/**
	 * Which session a request uses: "tsp", "customer" or "cms". An explicit
	 * authMode wins; otherwise the portal of the current page, so shared
	 * components (form viewer, bingo sheet) send the right token.
	 */
	portalScope(data) {
		const mode = (data && data.authMode) || this.authMode;
		if (mode === "tsp" || mode === "customer") return mode;
		const path = typeof window !== "undefined" ? window.location.pathname : "";
		if (path.indexOf("/tsp-portal") !== -1) return "tsp";
		if (path.indexOf("/customer-portal") !== -1) return "customer";
		return "cms";
	}

	isTspRequest(data) {
		return this.portalScope(data) === "tsp";
	}

	/** Drop the session of `scope` after a 401 or an expired token. */
	endSession(scope) {
		if (scope === "tsp") {
			this.clearTspTokens();
		} else if (scope === "customer") {
			clearCustomerSession();
			const path = window.location.pathname;
			const portalIndex = path.indexOf("/customer-portal");
			if (portalIndex !== -1 && path.indexOf("/customer-portal/sign_in") === -1) {
				window.location.replace(`${path.slice(0, portalIndex)}/customer-portal/sign_in`);
			}
		} else {
			store.dispatch(logout());
		}
	}

	readStorageValue(storage, key) {
		try {
			return storage.getItem(key) || "";
		} catch (_) {
			return "";
		}
	}

	getTspTokenFromProfile() {
		const rawProfile =
			this.readStorageValue(localStorage, "tsp_profile") ||
			this.readStorageValue(sessionStorage, "tsp_profile");
		try {
			const profile = rawProfile ? JSON.parse(rawProfile) : {};
			return profile.access_token || profile.accessToken || profile.token || "";
		} catch (_) {
			return "";
		}
	}

	getTspToken() {
		const token =
			this.readStorageValue(localStorage, "movegistics_tsp_access_token") ||
			this.readStorageValue(sessionStorage, "movegistics_tsp_access_token") ||
			this.readStorageValue(localStorage, "tsp_access_token") ||
			this.readStorageValue(sessionStorage, "tsp_access_token") ||
			this.getTspTokenFromProfile();
		if (token && token !== this.readStorageValue(localStorage, "movegistics_tsp_access_token")) {
			try {
				localStorage.setItem("movegistics_tsp_access_token", token);
				sessionStorage.setItem("movegistics_tsp_access_token", token);
				localStorage.removeItem("tsp_access_token");
				sessionStorage.removeItem("tsp_access_token");
			} catch (_) { }
		}
		return token;
	}

	clearTspTokens() {
		try {
			localStorage.removeItem("movegistics_tsp_access_token");
			sessionStorage.removeItem("movegistics_tsp_access_token");
			localStorage.removeItem("tsp_access_token");
			sessionStorage.removeItem("tsp_access_token");
		} catch (_) { }
	}

	setHeaders(data) {
		let headers = {};
		headers["accept-language"] = "en";
		headers["device_type"] = "CMS";
		headers["Content-Type"] = "application/json";
		const scope = this.portalScope(data);
		const isTspRequest = scope === "tsp";

		if (data) {
			if (data.isMultipart) {
				headers["Content-Type"] = "multipart/form-data";
			}

			if (data.headers) {
				for (var key in data.headers) {
					if (data.headers.hasOwnProperty(key)) {
						headers[key] = data.headers[key];
					}
				}
			}
		}

		if (isTspRequest && !(data && data.skipAuth)) {
			const tspToken = this.getTspToken();
			if (tspToken) {
				headers["Authorization"] = `Bearer ${tspToken}`;
				headers["access-token"] = tspToken;
			}
		} else if (scope === "customer") {
			const customerToken = !(data && data.skipAuth) && getCustomerToken();
			if (customerToken) headers["access_token"] = customerToken;
		} else if (localStorage.getItem("isLogin") === "true" && !(data && data.skipAuth)) {
			headers["access_token"] = localStorage.getItem("access_token");
			headers["admin_id"] = localStorage.getItem("adminid");
		}

		if (this.userType) {
			headers["user_type"] = this.userType;
		}

		return headers;
	}
}

// SAMPLE DATA JSON
/* let sample_data = {

		// ADDITIONAL HEADERS IF REQUIRED
		headers :{
				'Content-type'  : 'xx-urlencode',
		},

		// IF USER ID AND TOKEN SHOULDN'T BE PASSED IN HEADERS (USER FOR AFTER LOGIN API)
		// DEFAULT : FALSE;
		skipAuth    : false,

		// IF Default error handling needs to be overridden
		// DEFAULT : FALSE;
		skipErrorHandling    : false,

		// FOR SENDING FILES OR FORM DATA REQUEST
		isMultipart : true,

		// `PARAMS` ARE THE URL PARAMETERS TO BE SENT WITH THE REQUEST
		params : {
				user_id     : 10,
				name        : "lorem",
				page        : 3,
				sort_by     : 'name'
		},

		// POST DATA
		data : {
				firstName   : 'Lorem',
				lastName    : 'Ipsum'
		},
} */

export default Api;
